import { useEffect } from "react";
import TampaHeroSection from "./components/HeroSection";
import TampaStickyNav from "./components/StickyNav";
import TampaProcessingTimeSection from "./components/ProcessingTimeSection";
import TampaServicesSection from "./components/ServicesSection";
import TampaLocalAreaSection from "./components/LocalAreaSection";
import TampaTestimonialsSection from "./components/TestimonialsSection";
import TampaAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import TampaComparisonSection from "./components/ComparisonSection";
import TampaMistakesSection from "./components/MistakesSection";
import TampaHowItWorksSection from "./components/HowItWorksSection";
import TampaFAQSection, { faqs } from "./components/FAQSection";
import TampaHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import TampaFinalCTA from "./components/FinalCTA";
import TampaTravelTimingSection from "./components/TravelTimingSection";
import TampaDestinationsSection from "./components/DestinationsSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function TampaPage() {
  useEffect(() => {
    document.title = "Emergency Passport Tampa, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/tampa/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Tampa, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Tampa Bay.");
    setMeta("geo.placename", "Tampa, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "27.9506;-82.4572");
    setMeta("ICBM", "27.9506, -82.4572");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Tampa Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center has a physical office at 400 N Ashley Dr #1700, Tampa, FL 33602. Emergency and expedited passport services as fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        address: {
          "@type": "PostalAddress",
          streetAddress: "400 N Ashley Dr #1700",
          addressLocality: "Tampa",
          addressRegion: "FL",
          postalCode: "33602",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 27.9506,
          longitude: -82.4572,
        },
        areaServed: {
          "@type": "City",
          name: "Tampa",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Tampa",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <TampaHeroSection />
      <TampaStickyNav />
      <TampaProcessingTimeSection />
      <TampaServicesSection />
      <TampaLocalAreaSection />
      <TampaTravelTimingSection />
      <TampaDestinationsSection />
      <TampaTestimonialsSection />
      <TampaAcceptanceFacilitiesSection />
      <TampaComparisonSection />
      <TampaMistakesSection />
      <TampaHowItWorksSection />
      <TampaFAQSection />
      <TampaHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/tampa" state="FL" />
      <TampaFinalCTA />
    </div>
  );
}